import { useColumnPreferencesContext } from "@context-providers/table/column-preferences-provider";
import { updatePreferencesWithOrder } from "@context-providers/table/column-preferences-utils";
import { FilterMenu, Option } from "@flat-ui";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  setIsFilterMenuOpen,
  setOriginalColumnPreferences,
} from "@store/table/table-slice";
import { TableEvents, TableType } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface Props<K extends string> {
  /** Mapping of column field names to human-readable labels */
  labelsMap: Record<K, string>;

  /** Optional list of columns that should be disabled (always visible, cannot be toggled) */
  disabledColumns?: K[];

  /** The type of table using this menu (use e.g. for tracking) */
  tableType: TableType;
}

type ColumnOption<K extends string> = Pick<Option, "label" | "isDisabled"> & {
  key: K;
  value: K;
};

export function FaroTableColumnVisibilityMenu<K extends string>({
  labelsMap,
  disabledColumns = [],
  tableType,
}: Props<K>): JSX.Element {
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();
  const {
    updateColumnPreferences,
    updateColumnPreferencesLocally,
    columnPreferences,
    dataColumns,
  } = useColumnPreferencesContext();

  const { isOpen, anchorPosition, originalColumnPreferences } = useAppSelector(
    (state) => state.table.filterMenu
  );

  function handleCloseMenu(): void {
    if (!columnPreferences) {
      dispatch(setIsFilterMenuOpen(false));
      dispatch(setOriginalColumnPreferences(undefined));
      return;
    }

    // Check if visibility changed, track and save
    if (originalColumnPreferences) {
      const changedColumns = Object.keys(columnPreferences).filter((key) => {
        const isCurrentColumnVisible = columnPreferences[key]?.visible;
        const isOriginalColumnVisible = originalColumnPreferences[key]?.visible;
        return (
          isCurrentColumnVisible !== undefined &&
          isCurrentColumnVisible !== isOriginalColumnVisible
        );
      });

      // Track each changed column
      for (const key of changedColumns) {
        trackEvent({
          name: TableEvents.changeColumnVisibility,
          props: {
            type: tableType,
            column: key,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            value: columnPreferences[key]?.visible,
          },
        });
      }

      if (changedColumns.length > 0) {
        void updateColumnPreferences(columnPreferences);
      }
    }

    dispatch(setIsFilterMenuOpen(false));
    dispatch(setOriginalColumnPreferences(undefined));
  }

  function handleFilterChange(newSelected: ColumnOption<K>[]): void {
    if (!columnPreferences) {
      return;
    }

    // Build new visibility state based on selected options
    const selectedKeys = new Set(newSelected.map((opt) => opt.key));

    // Update all preferences with new visibility
    const updatedPreferences = updatePreferencesWithOrder(
      columnPreferences,
      selectedKeys
    );

    updateColumnPreferencesLocally(updatedPreferences);
  }

  const disabledColumnsSet = new Set(disabledColumns);

  // Convert availableColumns to FilterMenu format
  const options: ColumnOption<K>[] = dataColumns
    .filter((field): field is K => field in labelsMap)
    .map((field) => ({
      key: field,
      value: field,
      label: labelsMap[field],
      isDisabled: disabledColumnsSet.has(field),
    }));

  // Convert columnPreferences to selectedOptions
  const selectedOptions = options.filter(
    (option) => columnPreferences?.[option.key]?.visible === true
  );

  return (
    <FilterMenu
      open={isOpen}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      headingLabel="Select Columns"
      options={options}
      selectedOptions={selectedOptions}
      onFilterChange={handleFilterChange}
      onFilterMenuClose={handleCloseMenu}
      showSearch={false}
      showClearAll={false}
    />
  );
}
